import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

const NotificationBell: React.FC = () => {
  const {
    unreadCount,
    notifications,
    browserNotificationsEnabled,
    requestNotificationPermission,
    markAsRead,
    markAllAsRead,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNotificationClick = async (notif: any) => {
    const waNumberId = typeof notif.relatedEntities?.whatsAppNumberId === 'object'
      ? notif.relatedEntities.whatsAppNumberId?._id
      : notif.relatedEntities?.whatsAppNumberId;
    const contactId = typeof notif.relatedEntities?.contactId === 'object'
      ? notif.relatedEntities.contactId?._id
      : notif.relatedEntities?.contactId;

    if (!notif.isRead && notif._id) {
      try { await markAsRead(notif._id); } catch {}
    }

    if (contactId && waNumberId) navigate(`/chat/${waNumberId}/${contactId}`);
    else if (waNumberId) navigate(`/chat/${waNumberId}`);
    else navigate('/notifications');

    setOpen(false);
  };

  const recentNotifs = notifications.slice(0, 5);
  const hasUnread = unreadCount > 0;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        style={{
          width: 36, height: 36, borderRadius: 8,
          border: 'none',
          background: open ? '#f1f5f9' : 'transparent',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#64748b',
          transition: 'all 150ms ease',
          position: 'relative',
          fontFamily: 'Inter, sans-serif',
        }}
        onMouseEnter={e => {
          if (!open) {
            (e.currentTarget).style.background = '#f1f5f9';
            (e.currentTarget).style.color = '#0f172a';
          }
        }}
        onMouseLeave={e => {
          if (!open) {
            (e.currentTarget).style.background = 'transparent';
            (e.currentTarget).style.color = '#64748b';
          }
        }}
      >
        {/* Bell icon (SVG) */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {hasUnread ? (
            <>
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              <circle cx="18" cy="5" r="4" fill="#ef4444" stroke="none" />
            </>
          ) : (
            <>
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </>
          )}
        </svg>

        {/* Badge */}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: 4, right: 4,
            minWidth: 16, height: 16,
            borderRadius: 9999,
            background: '#ef4444',
            color: 'white',
            fontSize: '0.6rem',
            fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px',
            border: '2px solid white',
            lineHeight: 1,
            animation: 'scaleIn 0.2s ease-out',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 360,
          maxHeight: 480,
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: 16,
          boxShadow: '0 20px 48px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9999,
          overflow: 'hidden',
          animation: 'scaleIn 0.15s ease-out',
          transformOrigin: 'top right',
        }}>

          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px 10px',
            borderBottom: '1px solid #f1f5f9',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#0f172a' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <span style={{
                  padding: '2px 8px', borderRadius: 9999,
                  background: '#fef2f2', color: '#ef4444',
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={async () => { try { await markAllAsRead(); } finally { setOpen(false); } }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: 600, color: '#22c55e',
                  fontFamily: 'Inter, sans-serif', padding: 0,
                  transition: 'color 150ms ease',
                }}
                onMouseEnter={e => { (e.currentTarget).style.color = '#16a34a'; }}
                onMouseLeave={e => { (e.currentTarget).style.color = '#22c55e'; }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {recentNotifs.length === 0 ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '3rem 1rem', gap: 8, color: '#94a3b8',
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14, background: '#f8fafc',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#64748b' }}>All caught up!</span>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>No notifications yet</span>
              </div>
            ) : (
              recentNotifs.map((notif) => (
                <div
                  key={notif._id}
                  onClick={() => handleNotificationClick(notif)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '12px 16px',
                    cursor: 'pointer',
                    background: notif.isRead ? 'transparent' : 'rgba(34,197,94,0.04)',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 150ms ease',
                    position: 'relative',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = notif.isRead ? 'transparent' : 'rgba(34,197,94,0.04)'; }}
                >
                  {/* Unread indicator */}
                  {!notif.isRead && (
                    <span style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
                      background: '#22c55e', borderRadius: '0 2px 2px 0',
                    }} />
                  )}

                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: '#f0fdf4',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: notif.isRead ? 500 : 600,
                      color: '#0f172a',
                      marginBottom: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {notif.title}
                    </div>
                    <div style={{
                      fontSize: '0.8125rem', color: '#64748b',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {notif.message}
                    </div>
                  </div>

                  {/* Unread dot */}
                  {!notif.isRead && (
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: '#22c55e', flexShrink: 0, marginTop: 4,
                      boxShadow: '0 0 6px rgba(34,197,94,0.5)',
                    }} />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            gap: 8,
            flexShrink: 0,
          }}>
            {!browserNotificationsEnabled && (
              <button
                onClick={async () => { await requestNotificationPermission(); setOpen(false); }}
                style={{
                  flex: 1,
                  padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
                  background: 'white', color: '#475569', fontSize: '0.8125rem', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 150ms ease',
                }}
                onMouseEnter={e => { (e.currentTarget).style.background = '#f8fafc'; }}
                onMouseLeave={e => { (e.currentTarget).style.background = 'white'; }}
              >
                🔔 Enable browser notifications
              </button>
            )}
            <button
              onClick={() => { navigate('/notifications'); setOpen(false); }}
              style={{
                flex: 1,
                padding: '8px 12px', borderRadius: 8, border: 'none',
                background: '#22c55e', color: 'white', fontSize: '0.8125rem', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 150ms ease',
              }}
              onMouseEnter={e => { (e.currentTarget).style.background = '#16a34a'; }}
              onMouseLeave={e => { (e.currentTarget).style.background = '#22c55e'; }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
